import { useSession } from '@/auth/useSession.js';
import NotepadIcon from '@/components/assets/notepad-icon.svg?react';
import { EditAssessmentResultForm } from '@/components/feedbacks/edit-assessment-result-form.js';
import { MajorConcernIssues, StudentIssues, TechnicalIssues } from '@/components/feedbacks/feedback-issues.js';
import { ReadonlyIssuesList } from '@/components/feedbacks/feedback-issues-list.js';
import { DiagnosticDecodeGapSummary } from '@/components/feedbacks/diagnostic-decode-gap-summary.js';
import { ReadOnlyAssessmentResultForm } from '@/components/feedbacks/read-only-assessment-result-form.js';
import {
  getLastVisibleDiagnosticContentId,
  getMakeupDiagnosticResumeMessage,
  getVisibleDiagnosticAssessmentContents,
} from '@/components/feedbacks/diagnostic-word-list-visibility.js';
import {
  AssessmentReultsGrouped,
  AssessmentUiSections,
  getAssessmentUISection,
  isDiagnosticWordListVisible,
  SessionFeedbackFormSchema,
  shouldStopDiagnosticTesting,
  assessmentResultValueFieldKey,
  assessmentSectionTestIdKey,
  slugifyForTestId,
} from '@/components/feedbacks/session-feedback-utils.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { FormCheckboxGroup } from '@/components/forms/form-checkbox-group.js';
import { FormRadioGroup } from '@/components/forms/form-radio-group.js';
import { FormTextArea } from '@/components/forms/form-text-area.js';
import { InfoCard } from '@/components/info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { Button } from '@/components/ui/button.js';
import { booleanOnlyValueOf, buildBooleanSelectOptions } from '@/components/ui/multi-select-utils.js';
import { booleanToCustomString } from '@/lib/string-utils.js';
import { booleanFromString, distinct, isAirAdmin, isNotNullish, MultiMap } from '@airreading/common';
import {
  AssessmentContent,
  type DiagnosticDecodeGapSummary as DiagnosticDecodeGapSummaryData,
  SessionFeedbackIssue,
  SessionFeedbackIssueDetails,
  StudentYear,
  studentYearOption,
} from '@airreading/common/data';
import { InfoIcon } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';

/** External Airtable form for family trial (diagnostic) class feedback — scoped out of v2 for now. */
export const FAMILY_TRIAL_FEEDBACK_AIRTABLE_URL = 'https://airtable.com/appQhRezAYp3d7O40/shrTOZyYDwS4NwKzB';

export const SessionFeedbackForm = ({
  editing,
  form,
  studentAttended,
  issues,
  majorConcerns,
  otherTechIssues,
  feedbackContent,
  assessmentResults,
  assessmentContentsById,
  diagnosticCompletedFromOtherSession,
  diagnosticContentIdsCompletedInPriorSession = [],
  studentName,
  studentGrade,
  isFamilyClass = false,
  isFamilyTrialClass = false,
  valueTestId,
  fieldTestId,
  diagnosticDecodeGap = null,
}: {
  editing: boolean;
  form: UseFormReturn<SessionFeedbackFormSchema>;
  studentAttended: boolean | null;
  issues: SessionFeedbackIssue[];
  majorConcerns: string | null;
  otherTechIssues: string | null;
  feedbackContent: string | null;
  assessmentResults: AssessmentReultsGrouped;
  feedbackSubmitted: boolean;
  assessmentContentsById: Map<string, AssessmentContent>;
  diagnosticCompletedFromOtherSession: boolean;
  /** Makeup resume: hide these diagnostic word lists (completed in a prior incomplete session). */
  diagnosticContentIdsCompletedInPriorSession?: string[];
  studentName?: string | null;
  studentGrade?: string | null;
  /** Orgs with parentFeedback share notes with the student/family. */
  isFamilyClass?: boolean;
  /** Family Trial lesson classes use an external Airtable form instead of in-app notes. */
  isFamilyTrialClass?: boolean;
  valueTestId?: (section: string, field: string) => string;
  fieldTestId?: (section: string, field: string) => string;
  diagnosticDecodeGap?: DiagnosticDecodeGapSummaryData | null;
}) => {
  const { actingUser } = useSession();
  const isAdmin = isAirAdmin(actingUser);
  const { watch } = form;
  const hasMajorConcerns = watch('issues')?.includes('Strong_behavior_issues');
  const hasTechIssue = watch('issues')?.includes('tech_issue');
  const watchedNotFinishedUiSections = form.watch('assessmentNotFinishedUiSections');
  const watchedDiagnosticIncomplete = form.watch('diagnosticIncomplete');
  const studentAttendedValue = watch('studentAttended');
  const studentAttendedBoolean = studentAttendedValue ? booleanFromString(studentAttendedValue) : null;
  const assessmentSections = Array.from(
    MultiMap.groupBy(assessmentResults, ({ assessment }) => getAssessmentUISection(assessment)).entries(),
  ).toSorted(([sectionA], [sectionB]) => AssessmentUiSections[sectionA].order - AssessmentUiSections[sectionB].order);

  const sessionIncludesDiagnostic = assessmentSections.some(([uiSection]) => uiSection === 'Reading diagnostics');
  const priorCompletedDiagnosticContentIds = new Set(diagnosticContentIdsCompletedInPriorSession);

  const getStudentInfoLabel = (sectionTitle: string): string | null => {
    if (sectionTitle !== 'Grade level fluency' || (!studentName && !studentGrade)) {
      return null;
    }
    const namePart = studentName || '';
    const gradePart = studentGrade ? `${studentYearOption(studentGrade as StudentYear)} grade` : '';
    const separator = studentName && studentGrade ? ' : ' : '';
    return `${namePart}${separator}${gradePart}`;
  };

  return (
    <>
      <InfoCard headerText="Attendance" contentClass="grid-cols-2-auto-fix max-[900px]:grid-cols-1" showRedDot={false}>
        <InfoKeyValue
          label="Student attended?"
          value={
            <span data-testid={valueTestId?.('attendance', 'student-attended')}>
              {studentAttended == null ? '-' : booleanToCustomString(studentAttended)}
            </span>
          }
          edit={{
            editing,
            editModeComponent: (
              <FormRadioGroup
                name="studentAttended"
                label="Student attended"
                form={form}
                options={buildBooleanSelectOptions({
                  trueLabel: 'Present',
                  falseLabel: 'Absent',
                  trueStyle: 'bg-green-100 text-green-800 px-1 py-1',
                  falseStyle: 'bg-red-200 text-red-900 px-1 py-1',
                })}
                radioGroupProps={{ 'data-testid': 'feedback-radio-student-attended' }}
              />
            ),
          }}
        />
        {studentAttendedBoolean !== false && (
          <div className="flex flex-col gap-2 max-[900px]:gap-y-5">
            <InfoKeyValue
              label="Student issues (if any)"
              value={
                <ReadonlyIssuesList
                  issues={StudentIssues.filter((item) => issues.includes(item))}
                  testId={valueTestId?.('attendance', 'student-issues')}
                />
              }
              edit={{
                editing,
                editModeComponent: (
                  <FormCheckboxGroup
                    name="issues"
                    form={form}
                    options={StudentIssues}
                    label="Student issues (if any)"
                    optionLabel={(item) => SessionFeedbackIssueDetails[item].label}
                    labelClassName="bg-gray-100 px-2 py-1"
                    checkboxProps={{ 'data-testid': 'feedback-checkbox-student-issues' }}
                  />
                ),
              }}
            />
          </div>
        )}
        {hasTechIssue && studentAttendedBoolean !== false && (
          <div className="col-span-4 max-[900px]:col-span-1 grid grid-cols-2 max-[900px]:grid-cols-1 gap-x-6 gap-y-5">
            <InfoKeyValue
              label="Technical issues"
              value={
                <ReadonlyIssuesList
                  issues={TechnicalIssues.filter((item) => issues.includes(item))}
                  testId={valueTestId?.('attendance', 'technical-issues')}
                />
              }
              edit={{
                editing,
                editModeComponent: (
                  <FormCheckboxGroup
                    name="issues"
                    form={form}
                    options={TechnicalIssues}
                    label="Technical issues"
                    optionLabel={(item) => SessionFeedbackIssueDetails[item].label}
                    labelClassName="bg-gray-100 px-2 py-1"
                    checkboxProps={{ 'data-testid': 'feedback-checkbox-tech-issues' }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="Tech issues: Can you tell us more about the issues?"
              value={
                <span
                  className="whitespace-pre-wrap"
                  data-testid={valueTestId?.('attendance', 'technical-issues-notes')}
                >
                  {otherTechIssues ?? '-'}
                </span>
              }
              edit={{
                editing,
                editModeComponent: (
                  <FormTextArea
                    name="otherTechIssues"
                    form={form}
                    label="Tech issues: Can you tell us more about the issues?"
                    textAreaProps={{ 'data-testid': 'feedback-textarea-other-tech-issues' }}
                  />
                ),
              }}
            />
          </div>
        )}
        {hasMajorConcerns && studentAttendedBoolean !== false && (
          <div className="col-span-4 max-[900px]:col-span-1 grid grid-cols-2 max-[900px]:grid-cols-1 gap-x-6 gap-y-5">
            <InfoKeyValue
              label="Major class concerns"
              value={
                <ReadonlyIssuesList
                  issues={MajorConcernIssues.filter((item) => issues.includes(item))}
                  testId={valueTestId?.('attendance', 'major-concerns')}
                />
              }
              edit={{
                editing,
                editModeComponent: (
                  <FormCheckboxGroup
                    name="issues"
                    form={form}
                    options={MajorConcernIssues}
                    label="Major class concerns"
                    optionLabel={(item) => SessionFeedbackIssueDetails[item].label}
                    labelClassName="bg-gray-100 px-2 py-1"
                    checkboxProps={{ 'data-testid': 'feedback-checkbox-major-concerns' }}
                  />
                ),
              }}
            />
            <InfoKeyValue
              label="Major class concerns: Can you tell us more about the issues?"
              value={
                <span className="whitespace-pre-wrap" data-testid={valueTestId?.('attendance', 'major-concerns-notes')}>
                  {majorConcerns ?? '-'}
                </span>
              }
              edit={{
                editing,
                editModeComponent: (
                  <FormTextArea
                    name="majorConcerns"
                    form={form}
                    label="Major class concerns: Can you tell us more about the issues?"
                    textAreaProps={{ 'data-testid': 'feedback-textarea-major-concerns' }}
                  />
                ),
              }}
            />
          </div>
        )}
      </InfoCard>
      {assessmentResults.length === 0 && isAdmin && !isFamilyTrialClass && (
        <InfoCard headerText="No assessments were taken during this session." showRedDot={false}>
          {null}
        </InfoCard>
      )}
      {studentAttendedBoolean !== false &&
        assessmentSections.map(([uiSection, assessmentsInSection]) => {
          if (assessmentsInSection.length === 0) {
            return null;
          }

          const { showAssessmentLabel, showContentLabel, title, checkboxMessage } = AssessmentUiSections[uiSection];

          if (sessionIncludesDiagnostic && diagnosticCompletedFromOtherSession) {
            // This means the student has already completed diagnostic assessment but in another session
            // We need to show this message below instead of the diagnostic form

            if (uiSection !== 'Reading diagnostics') {
              // hide all other sections
              return null;
            }

            // only show the message for diagnostics section
            return (
              <InfoCard key={title} headerText={title} showRedDot={false}>
                <div className="flex flex-col items-center justify-center text-center space-y-4">
                  <NotepadIcon className="w-16 h-20" />
                  <div>
                    <div>
                      This student has completed the Diagnostic Assessment. Skip the diagnostic and continue to the
                      Teacher Read Aloud section.
                    </div>
                    <div>Each Makeup Diagnostic lesson has a different Teacher Read Aloud.</div>
                  </div>
                </div>
              </InfoCard>
            );
          }

          const studentInfoLabel = getStudentInfoLabel(title);
          const sectionTestIdKey = assessmentSectionTestIdKey(title);
          const makeupDiagnosticResumeMessage =
            title === 'Reading diagnostics'
              ? getMakeupDiagnosticResumeMessage(
                  diagnosticContentIdsCompletedInPriorSession,
                  assessmentsInSection.flatMap(({ results }) =>
                    results
                      .map((result) => assessmentContentsById.get(result.assessmentContentId))
                      .filter(isNotNullish),
                  ),
                )
              : null;

          return (
            <InfoCard
              key={title}
              headerText={title}
              headerSubtitle={studentInfoLabel}
              showRedDot={false}
              maxItemsPerRow={2}
              contentClass="max-[900px]:grid-cols-1-auto-fix"
              footerContent={
                title !== 'Reading diagnostics' ? (
                  <FormCheckbox
                    label={`Student did not complete ${checkboxMessage || title}`}
                    form={form}
                    name={'assessmentNotFinishedUiSections'} // name does not matter here because we will override default checkbox behavior
                    horizontal
                    checkboxProps={{
                      onCheckedChange: (checked) => {
                        const current = watchedNotFinishedUiSections;
                        form.setValue(
                          'assessmentNotFinishedUiSections',
                          booleanOnlyValueOf(checked)
                            ? [...current, title].filter(distinct())
                            : current.filter((v) => v !== title),
                        );
                      },
                      disabled: !editing,
                      checked: watchedNotFinishedUiSections.includes(title),
                      'data-testid':
                        fieldTestId?.(sectionTestIdKey, 'checkbox-section-incomplete') ??
                        `feedback-checkbox-student-did-not-complete-${title.toLowerCase().replace(/\s+/g, '-')}`,
                    }}
                  />
                ) : null
              }
              headerClass="max-[900px]:flex-col max-[900px]:gap-y-2 max-[900px]:items-start"
            >
              <>
                {title === 'Reading diagnostics' && diagnosticDecodeGap && (
                  <div className="col-span-2 max-[900px]:col-span-1">
                    <DiagnosticDecodeGapSummary
                      gap={diagnosticDecodeGap}
                      testId={valueTestId?.(sectionTestIdKey, 'decode-gap')}
                      infoButtonTestId={fieldTestId?.(sectionTestIdKey, 'button-decode-gap-info')}
                    />
                  </div>
                )}
                {makeupDiagnosticResumeMessage && (
                  <p
                    className="col-span-2 max-[900px]:col-span-1 rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900"
                    data-testid="feedback-reading-diagnostics-callout-resume"
                  >
                    {makeupDiagnosticResumeMessage}
                  </p>
                )}
                {assessmentsInSection.map(({ assessment, results, assessmentIndex }) => {
                  const allAssessmentContents = results
                    .map((r) => assessmentContentsById.get(r.assessmentContentId))
                    .filter(isNotNullish);

                  const visibleAssessmentContents = getVisibleDiagnosticAssessmentContents({
                    sortedAssessmentContents: allAssessmentContents,
                    assessmentIndex,
                    form,
                    priorCompletedContentIds: diagnosticContentIdsCompletedInPriorSession,
                  });

                  // Get the ID of the actual last diagnostic word list in the entire assessment
                  // allAssessmentContents is already sorted by order, so find the last diagnostic from there
                  const lastDiagnosticContentId = allAssessmentContents
                    .filter((content) => content.format === 'diagnostic_word_list')
                    .pop()?.id;

                  const lastVisibleDiagnosticContentId = getLastVisibleDiagnosticContentId(visibleAssessmentContents);

                  return results.map((result, resultIndex) => {
                    const assessmentDisplayName = assessment.displayName || assessment.name;
                    const content = assessmentContentsById.get(result.assessmentContentId);

                    if (
                      content?.format === 'diagnostic_word_list' &&
                      priorCompletedDiagnosticContentIds.has(content.id)
                    ) {
                      return null;
                    }

                    // Keep full-list index alignment with `resultIndex` (used by stop-testing / edit forms).
                    // Visibility uses the outer resultIndex; when that list is shown, all contents pass.
                    const sortedAssessmentContents = results
                      .map((r) => assessmentContentsById.get(r.assessmentContentId))
                      .filter(isNotNullish)
                      .filter((content, __, list) => {
                        if (content.format !== 'diagnostic_word_list') {
                          return true;
                        }

                        return isDiagnosticWordListVisible({
                          form,
                          sortedAssessmentContents: list,
                          resultIndex,
                          assessmentIndex,
                        });
                      });

                    if (!sortedAssessmentContents.some((c) => c.id === result.assessmentContentId)) {
                      return null;
                    }

                    // Compare by content id — index comparison breaks when a completed prefix is hidden.
                    const isLastVisibleDiagnostic =
                      title === 'Reading diagnostics' &&
                      content?.format === 'diagnostic_word_list' &&
                      content.id === lastVisibleDiagnosticContentId;

                    // Show checkbox if this is the last visible diagnostic (even if it's the actual last list)
                    // because the last visible list itself could be incomplete
                    // But hide it when the "Final list for the student" alert is shown
                    const isFinalListForStudent = shouldStopDiagnosticTesting({
                      sortedAssessmentContents,
                      resultIndex,
                      assessmentIndex,
                      form,
                    });
                    const shouldShowCheckbox = isLastVisibleDiagnostic && !isFinalListForStudent;

                    const lastVisibleListContent = lastVisibleDiagnosticContentId
                      ? (assessmentContentsById.get(lastVisibleDiagnosticContentId) ?? null)
                      : null;

                    const isActualLastList = lastVisibleListContent?.id === lastDiagnosticContentId;

                    // Adjust label based on whether this is the actual last list or if there are more lists after
                    const checkboxLabel = isActualLastList
                      ? `Student did not complete ${lastVisibleListContent?.name || 'this list'}`
                      : `Student did not complete ${lastVisibleListContent?.name || 'this list'} and beyond`;

                    // For diagnostic word lists: disable the last visible list when diagnosticIncomplete is checked
                    // This prevents teachers from editing it after marking the diagnostic as incomplete
                    const isDiagnosticNotFinished =
                      title === 'Reading diagnostics' &&
                      content?.format === 'diagnostic_word_list' &&
                      isLastVisibleDiagnostic &&
                      watchedDiagnosticIncomplete;

                    const resultFieldKey = content
                      ? assessmentResultValueFieldKey(content.format, content.name)
                      : 'result';
                    const resultValueTestId = valueTestId?.(sectionTestIdKey, resultFieldKey);
                    const resultNestedValueTestId = (subfield: string) =>
                      valueTestId?.(sectionTestIdKey, `${resultFieldKey}-${subfield}`);

                    return (
                      <div key={result.assessmentContentId} className="col-span-2 max-[900px]:col-span-1">
                        <InfoKeyValue
                          label={showAssessmentLabel ? assessmentDisplayName : ''}
                          labelBold={false}
                          value={
                            <>
                              {showContentLabel && content?.name && (
                                <span
                                  className="font-bold"
                                  data-testid={valueTestId?.(
                                    sectionTestIdKey,
                                    `label-${slugifyForTestId(content.name)}`,
                                  )}
                                >
                                  {content.name}
                                </span>
                              )}
                              <ReadOnlyAssessmentResultForm
                                result={result}
                                sortedAssessmentContents={sortedAssessmentContents}
                                resultIndex={resultIndex}
                                assessmentDisplayName={assessmentDisplayName}
                                testId={resultValueTestId}
                                nestedValueTestId={resultNestedValueTestId}
                                studentGrade={studentGrade}
                              />
                            </>
                          }
                          edit={{
                            editing,
                            editModeComponent: (
                              <EditAssessmentResultForm
                                result={result}
                                form={form}
                                assessmentIndex={assessmentIndex}
                                resultIndex={resultIndex}
                                sortedAssessmentContents={sortedAssessmentContents}
                                lastDiagnosticContentId={lastDiagnosticContentId}
                                assessmentNotFinished={
                                  watchedNotFinishedUiSections.includes(title) || isDiagnosticNotFinished
                                }
                                assessmentName={assessment.name}
                                assessmentDisplayName={showAssessmentLabel ? assessmentDisplayName : undefined}
                                assessmentContentClass="max-[900px]:flex-col max-[900px]:gap-0"
                                isFinalListForStudent={isFinalListForStudent}
                                studentGrade={studentGrade}
                              />
                            ),
                          }}
                        />
                        {shouldShowCheckbox && editing && (
                          <div className="mt-4">
                            <FormCheckbox
                              label={checkboxLabel}
                              form={form}
                              name="diagnosticIncomplete"
                              horizontal
                              checkboxProps={{ 'data-testid': 'feedback-checkbox-diagnostic-incomplete' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  });
                })}
              </>
            </InfoCard>
          );
        })}
      {isFamilyTrialClass ? (
        <InfoCard headerText="Trial feedback" showRedDot={false}>
          <div className="col-span-4">
            <Button
              type="button"
              size="sm"
              variant="destructive"
              data-testid="feedback-button-family-trial-airtable"
              onClick={() => window.open(FAMILY_TRIAL_FEEDBACK_AIRTABLE_URL, '_blank', 'noopener,noreferrer')}
            >
              Fill out feedback
            </Button>
          </div>
        </InfoCard>
      ) : (
        <InfoCard headerText={isFamilyClass ? 'Family student feedback' : 'Class notes'} showRedDot={false}>
          <InfoKeyValue
            label=""
            value={
              <span className="whitespace-pre-wrap" data-testid={valueTestId?.('class-notes', 'content')}>
                {feedbackContent ?? '-'}
              </span>
            }
            className={'col-span-4'}
            edit={{
              editing,
              editModeComponent: (
                <div className="col-span-4">
                  <div className="text-sm">
                    {isFamilyClass
                      ? 'Feedback is shared directly with the student and family.'
                      : 'Please note any additional information about the student below.'}
                  </div>
                  <FormTextArea
                    name="feedbackContent"
                    form={form}
                    label=""
                    textAreaProps={{ 'data-testid': 'feedback-textarea-class-notes' }}
                  />
                  <div className="flex gap-2 text-sm text-muted-foreground">
                    <InfoIcon className="w-5 h-5" /> For urgent matters requiring immediate attention, please submit a
                    support ticket.
                  </div>
                </div>
              ),
            }}
          />
        </InfoCard>
      )}
    </>
  );
};
